import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';

// Layouts
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { FullPageLoader } from './components/UI/Spinner';

// Pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { Dashboard } from './pages/dashboard/Dashboard';
import { CustomerList } from './pages/customers/CustomerList';
import { ProjectList } from './pages/projects/ProjectList';
import { ProductList } from './pages/inventory/ProductList';
import { QuoteList } from './pages/quotes/QuoteList';
import { QuoteForm } from './pages/quotes/QuoteForm';
import { QuoteDetail } from './pages/quotes/QuoteDetail';
import { InvoiceList } from './pages/invoices/InvoiceList';
import { InvoiceForm } from './pages/invoices/InvoiceForm';
import { InvoiceDetail } from './pages/invoices/InvoiceDetail';
import { Subscriptions } from './pages/subscriptions/Subscriptions';
import { Profile } from './pages/profile/Profile';
import { Landing } from './pages/Landing';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Support } from './pages/Support';
import { RequireVerification } from './components/RequireVerification';

// Admin Pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminInvoices } from './pages/admin/AdminInvoices';
import { AdminQuotes } from './pages/admin/AdminQuotes';
import { AdminCustomers } from './pages/admin/AdminCustomers';
import { AdminProjects } from './pages/admin/AdminProjects';
import { AdminInventory } from './pages/admin/AdminInventory';
import { AdminSubscriptions } from './pages/admin/AdminSubscriptions';
import { AdminCurrencies } from './pages/admin/AdminCurrencies';
import { AdminSystem } from './pages/admin/AdminSystem';
import { NotFound } from './pages/NotFound';

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <FullPageLoader message="Cargando tu sesión en Quinvoice..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Only Route (for login/register when already authenticated)
const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <FullPageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Redirect root to dashboard if logged in, otherwise Landing is shown
// Actually we can just show Landing to everyone, but login/register redirect to /dashboard.
// The user asked for the landing page in `/`. We'll just render it directly.

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Auth Routes */}
              <Route
                path="/login"
                element={
                  <PublicOnlyRoute>
                    <Login />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicOnlyRoute>
                    <Register />
                  </PublicOnlyRoute>
                }
              />

              {/* Public Landing & Static Pages */}
              <Route path="/" element={<Landing />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/support" element={<Support />} />

              {/* Protected App Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Standard App Pages */}
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* Entity Routes - Require Verification */}
                <Route path="/customers" element={<RequireVerification><CustomerList /></RequireVerification>} />
                <Route path="projects" element={<RequireVerification><ProjectList /></RequireVerification>} />
                <Route path="inventory" element={<RequireVerification><ProductList /></RequireVerification>} />
                
                {/* Quotes */}
                <Route path="quotes" element={<RequireVerification><QuoteList /></RequireVerification>} />
                <Route path="quotes/create" element={<RequireVerification><QuoteForm /></RequireVerification>} />
                <Route path="quotes/:id" element={<RequireVerification><QuoteDetail /></RequireVerification>} />
                <Route path="quotes/edit/:id" element={<RequireVerification><QuoteForm /></RequireVerification>} />

                {/* Invoices */}
                <Route path="invoices" element={<RequireVerification><InvoiceList /></RequireVerification>} />
                <Route path="invoices/create" element={<RequireVerification><InvoiceForm /></RequireVerification>} />
                <Route path="invoices/:id" element={<RequireVerification><InvoiceDetail /></RequireVerification>} />
                <Route path="invoices/edit/:id" element={<RequireVerification><InvoiceForm /></RequireVerification>} />

                {/* Subscriptions & Settings */}
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="profile" element={<Profile />} />

                {/* Admin Management Panel */}
                <Route path="admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="inventory" element={<AdminInventory />} />
                  <Route path="subscriptions" element={<AdminSubscriptions />} />
                  <Route path="currencies" element={<AdminCurrencies />} />
                  <Route path="system" element={<AdminSystem />} />
                </Route>

                {/* 404 in layout */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
